/**
 * @file: useConstructionJob.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { PredefinedAnnotationTags } from '@euler/app/flows/media/annotation/functions/tagPresets';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { useUserLocalSettings } from '@euler/app/flows/settings/functions/useUserLocalSettings';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { isMediaStagedPending } from '@euler/app/flows/task/functions/task-detail/util';
import { useProductManifestItemsForSubject } from '@euler/app/flows/task/functions/useProductManifestItemsForSubject';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { HttpError } from '@euler/lib/request';
import { OrgMember } from '@euler/model/entity';
import { CommonTaskStatus, ImageAnnotationScene } from '@euler/model/enum';
import { delayed, idEquals, onErrorIgnore } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConstructionJobActions } from './useConstructionJobActions';

export const useConstructionJob = (jobId: number) => {
  useToggleAutomaticKeyboard(true);

  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const appLoading = useAppLoading();
  const alert = useAlert();
  const confirm = useConfirm();

  const { taskManager, detail, profile, storeConfiguration } = useTaskContext();
  const { constructionManager } = taskManager;
  const [titleLoading, setTitleLoading] = useState(false);
  const [isTechModalVisible, setIsTechModalVisible] = useState(false);

  const job = detail.construction.jobs.find(x => x.id === jobId);
  const jobName = job?.name ?? '';
  const productManifestItems = useProductManifestItemsForSubject(jobName);

  const [{ noAlertBeforeNavigateAwayConstructionJob }, updateSettings] =
    useUserLocalSettings();

  const jobManager = useMemo(
    () => constructionManager.getConstructionJobManager(jobId),
    [constructionManager, jobId],
  );

  const [staged] = useBehaviorSubject(jobManager.staged$);
  const [dirty] = useBehaviorSubject(jobManager.dirty$);

  const fetchMediasFromInspectionResults = usePersistFn(async () => {
    if (staged.procedures.some(x => x.type === 'before')) {
      try {
        setTitleLoading(true);
        await jobManager.fetchMediasFromInspectionResults();
        setTitleLoading(false);
      } catch (e) {
        setTitleLoading(false);
        void alert({ title: '出错了', message: (e as Error).message });
      }
    }
  });

  useEffect(() => {
    return navigation.addListener('beforeRemove', async e => {
      if (jobManager.disposed) {
        return;
      }

      if (dirty) {
        if (noAlertBeforeNavigateAwayConstructionJob) {
          return;
        }
        e.preventDefault();
        void alert({
          title: '提示',
          message: [
            '数据已修改，您所录入的信息已保存在本地缓存中，',
            '返回后并不会丢失，建议您及时保存数据，是否现在保存？',
          ].join(''),
          buttons: [
            {
              text: '返回并放弃修改',
              style: 'destructive',
              onPress: async () => {
                // go back directly
                void jobManager.discardCache().catch(onErrorIgnore);
                constructionManager.endConstructionJob(jobId);
                navigation.dispatch(e.data.action);
              },
            },
            {
              text: '保存修改并返回',
              style: 'default',
              onPress: async () => {
                try {
                  appLoading.show();
                  await jobManager.commit({
                    saveOnly: true,
                    withPatching: true,
                  });
                  appLoading.hide();
                  delayed(() => {
                    constructionManager.endConstructionJob(jobId);
                    navigation.dispatch(e.data.action);
                  }, 150);
                } catch (err) {
                  appLoading.hide();
                  void alert({
                    title: '出错了',
                    message: (err as Error).message,
                  });
                }
              },
            },
            {
              text: '直接返回',
              style: 'default',
              onPress: () => {
                // go back directly
                constructionManager.endConstructionJob(jobId);
                navigation.dispatch(e.data.action);
              },
            },
            {
              text: '直接返回并不再提示',
              onPress: () => {
                // go back directly
                updateSettings({
                  noAlertBeforeNavigateAwayConstructionJob: true,
                });
                constructionManager.endConstructionJob(jobId);
                navigation.dispatch(e.data.action);
              },
            },
            {
              text: '取消',
              style: 'cancel',
            },
          ],
        });
      } else {
        constructionManager.endConstructionJob(jobId);
      }
    });
  }, [
    appLoading,
    dirty,
    jobId,
    jobManager,
    navigation,
    constructionManager,
    noAlertBeforeNavigateAwayConstructionJob,
    updateSettings,
    alert,
  ]);

  const tryLoadCached = usePersistFn(() => {
    jobManager
      .loadCached()
      .then(async cached => {
        if (cached == null) {
          const procedure = staged.procedures.find(x => x.type === 'before');
          if (procedure != null && procedure.medias.length === 0) {
            await fetchMediasFromInspectionResults();
          }

          return;
        }
        const shouldApplyCached = await confirm({
          title: '确认',
          message: '存在已录入的缓存数据，是否加载?',
          confirmButtonText: '立即加载',
          cancelButtonText: '忽略',
        });
        if (shouldApplyCached) {
          jobManager.applyCached(cached);
        } else {
          await jobManager.discardCache();
        }
      })
      .catch(onErrorIgnore);
  });

  useEffect(() => {
    tryLoadCached();
  }, [tryLoadCached]);

  const onResolveItemPress = useCallback(
    (item: string) => {
      jobManager.produce(draft => {
        const index = draft.context.resolved?.indexOf(item);
        if (index != null && index >= 0) {
          draft.context.resolved?.splice(index, 1);
        } else {
          if (draft.context.resolved == null) {
            draft.context.resolved = [item];
          } else {
            draft.context.resolved.push(item);
          }
        }
      });
    },
    [jobManager],
  );

  const onTechnicianSelected = usePersistFn((tech: OrgMember) => {
    setIsTechModalVisible(false);

    if (tech.id !== staged.technicianId) {
      if (
        storeConfiguration.restrictedRolesForUpdateConstructionJobAssignment
          ?.length
      ) {
        const roleHelper = new OrgUserRoleHelper(profile.user.role);
        if (
          !roleHelper.matchesAny(
            storeConfiguration.restrictedRolesForUpdateConstructionJobAssignment,
          )
        ) {
          void alert({
            title: '您没有权限修改当前项目派工',
            message: '若需帮助，请联系门店系统管理员！',
          });
          return;
        }
      }

      jobManager.produce(draft => {
        draft.technicianId = tech.id;
        draft.technicianName = tech.name;
      });
    }
  });

  const onTechnicianPickerPress = useCallback(() => {
    setIsTechModalVisible(true);
  }, []);

  const onTechnicianModalDismiss = usePersistFn(() => {
    setIsTechModalVisible(false);
  });

  const appNavigation = useAppNavigation();

  const watermark = useCallback(
    (scale?: number) =>
      job != null ? (
        <TaskImageWatermark
          licensePlateNo={detail.basicInfo.licensePlateNo}
          title={job?.name}
          author={profile.user.name}
          store={profile.store.name}
          type="construction"
          scale={scale}
        />
      ) : undefined,
    [
      detail.basicInfo.licensePlateNo,
      job,
      profile.store.name,
      profile.user.name,
    ],
  );

  const takeMedia = useMediaCaptureWithAnnotation({
    scene: ImageAnnotationScene.Construction,
  });

  const onTakeMedia = useCallback(
    (procedure: string, allowChooseMediaFromGallery: boolean) => {
      takeMedia({
        mediaCaptureRequired: !allowChooseMediaFromGallery,
        tags: () => PredefinedAnnotationTags.construction,
        defaultTag: () => ({
          name: procedure,
          color: procedure === '检查情况' ? 'warning' : 'green',
        }),
        watermark,
        uploadVideoFile: async (localFileUri, contentType, rotate) => {
          taskManager.uploadController.enqueue({
            localFileUri,
            contentType,
            type: 'video',
            parameters: {
              cover: true,
              blurhash: true,
              videoRotate: rotate,
            },
            context: {
              type: 'construction-job',
              jobId,
              procedure,
            },
          });
        },
        handleImageAnnotationResults: async results => {
          for (const result of results) {
            taskManager.uploadController.enqueue({
              localFileUri: result.imageUri,
              contentType: 'image/jpeg',
              type: 'image',
              parameters: {
                cover: true,
                blurhash: true,
              },
              context: {
                type: 'construction-job',
                jobId,
                procedure,
                annotationMetadata: result.annotationMetadata,
              },
              // eslint-disable-next-line @typescript-eslint/no-loop-func
              listener: (e, task) => {
                if (e.type === 'finished') {
                  // upload the original image.
                  const media = jobManager.findMedia(procedure, task.key);

                  if (!media) return;

                  if (media.annotationMetadata?.origin_uri) return;

                  if (result.originalImageUri != null) {
                    taskManager.largeMediaUploadController.enqueue({
                      localFileUri: result.originalImageUri,
                      contentType: 'image/jpeg',
                      type: 'image',
                      parameters: {
                        blurhash: true,
                      },
                      onSuccess: ({ result: { url } }) => {
                        jobManager.produceProcedure(
                          procedure,
                          (draft, original) => {
                            const index = original.medias.findIndex(
                              x => x._key === media._key,
                            );
                            const metadata =
                              draft.medias[index]?.annotationMetadata;
                            if (metadata) {
                              metadata.origin_uri = url;
                            }
                          },
                          { ignoreError: true },
                        );
                      },
                    });
                  }
                }
              },
            });
          }
        },
      });
    },
    [
      jobId,
      jobManager,
      takeMedia,
      taskManager.largeMediaUploadController,
      taskManager.uploadController,
      watermark,
    ],
  );

  const onDeleteMedia = useCallback(
    async (procedure: string, media: MediaInfo) => {
      const label = media.type === 'image' ? '图片' : '视频';
      const confirmed = await confirm({
        title: '确认',
        message: `是否确认删除该${label}?`,
        confirmButtonText: '是',
        cancelButtonText: '否',
      });
      if (confirmed) {
        jobManager.removeMedia(procedure, media.key);
      }
    },
    [confirm, jobManager],
  );

  const onEditMedia = useCallback(
    (procedure: string, mediaInfo: MediaInfo) => {
      const mediaStaged = jobManager.findMedia(procedure, mediaInfo.key);
      if (!mediaStaged) return;
      const originalImageUri = mediaStaged.annotationMetadata?.origin_uri;
      appNavigation.navigate('_imageAnnotation', {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        imageUri: originalImageUri || mediaInfo.url,
        tags: PredefinedAnnotationTags.inspection,
        annotationMetadata: originalImageUri
          ? mediaStaged.annotationMetadata
          : undefined,
        watermark,
        onDone: results => {
          if (
            JSON.stringify(mediaStaged.annotationMetadata ?? null) ===
            JSON.stringify(results.annotationMetadata ?? null)
          ) {
            return;
          }
          taskManager.uploadController.enqueue({
            localFileUri: results.imageUri,
            contentType: 'image/jpeg',
            type: 'image',
            // simulateFailure: true,
            parameters: {
              cover: true,
              blurhash: true,
            },
            context: {
              type: 'construction-job',
              jobId,
              procedure,
              key: mediaInfo.key,
            },
            listener: e => {
              if (e.type === 'finished') {
                if (mediaStaged.annotationMetadata?.origin_uri) return;

                // use the non-annotated media as the original url.
                if (mediaStaged.annotationMetadata == null) {
                  jobManager.produceProcedure(
                    procedure,
                    (draft, original) => {
                      const index = original.medias.findIndex(
                        x => x._key === mediaStaged._key,
                      );
                      const metadata = draft.medias[index]?.annotationMetadata;
                      if (metadata) {
                        metadata.origin_uri = mediaInfo.url;
                      }
                    },
                    { ignoreError: true },
                  );
                }
              }
            },
          });
          appNavigation.pop();
        },
      });
    },
    [appNavigation, jobId, jobManager, taskManager.uploadController, watermark],
  );

  const onRetryMediaUpload = useCallback(
    (procedure: string, media: MediaInfo) => {
      if (media.source?.type !== 'local') return;
      taskManager.uploadController.enqueue({
        localFileUri: media.source.localFileUri,
        contentType: media.source.contentType,
        type: media.type,
        parameters: media.source.uploadParameters,
        context: {
          type: 'construction-job',
          jobId,
          procedure,
          key: media.key,
        },
      });
    },
    [jobId, taskManager.uploadController],
  );

  const onPreviewVideo = useCallback(
    (procedure: string, media: MediaInfo) => {
      const stagedMedia = jobManager.findMedia(procedure, media.key);
      if (
        stagedMedia &&
        stagedMedia._source.type === 'local' &&
        stagedMedia._source.state.status === 'uploaded'
      ) {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: stagedMedia._source.localFileUri,
        });
      } else {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: media.url,
        });
      }
    },
    [appNavigation, jobManager],
  );

  const onRemarkTextChange = useCallback(
    (text: string) => {
      jobManager.produce(draft => {
        draft.remark = text;
      });
    },
    [jobManager],
  );

  const onProcedureMove = usePersistFn(
    (_procedure: string, index: number, dir: 'up' | 'down') => {
      const nextIndex = dir === 'up' ? index - 1 : index + 1;
      jobManager.produce(draft => {
        const temp = draft.procedures[nextIndex];
        draft.procedures[nextIndex] = draft.procedures[index];
        draft.procedures[index] = temp;
      });
    },
  );

  const onRemoveProcedure = usePersistFn(async (name: string) => {
    const confirmed = await confirm({
      title: '确认',
      message: '你即将移除该工序，移除后相关数据可能无法恢复，是否继续?',
      confirmButtonText: '确认',
      cancelButtonText: '取消',
    });
    if (confirmed) {
      jobManager.produce((draft, original) => {
        const index = original.procedures.findIndex(x => x.name === name);
        if (index < 0) {
          console.error('Cannot find procedure, this must be bug');
          return;
        }
        draft.procedures.splice(index, 1);
      });
    }
  });

  const onSave = usePersistFn(async () => {
    if (!dirty) return;
    try {
      appLoading.show();
      await jobManager.commit({ saveOnly: true, withPatching: true });
      appLoading.hide();
    } catch (e) {
      appLoading.hide();
      void alert({ title: '出错了', message: (e as Error).message });
    }
  });

  const onFinish = usePersistFn(async () => {
    if (!staged.procedures.length) {
      void alert({ title: '请至少添加一个工序!' });
      return;
    }
    for (const procedure of staged.procedures) {
      if (!procedure.medias.length) {
        void alert({
          title: '提示',
          message: `"${procedure.name}"工序还未上传图片或视频!`,
        });
        return;
      }
      for (const media of procedure.medias) {
        if (isMediaStagedPending(media)) {
          void alert({
            title: '提示',
            message: '有图片/视频正在上传，请稍候!',
          });
          return;
        }
      }
    }
    try {
      appLoading.show();
      await jobManager.commit({ saveOnly: false, withPatching: true });
      appLoading.hide();
      delayed(() => {
        navigation.goBack();
      }, 10);
    } catch (e) {
      appLoading.hide();
      void alert({ title: '出错了', message: (e as Error).message });
    }
  });

  const onAddProcedure = usePersistFn((name: string) => {
    if (staged.procedures.some(x => x.name === name)) {
      void alert({ title: '工序名称不能重复!' });
      return;
    }
    jobManager.produce(draft => {
      draft.procedures.push({
        name,
        type: 'custom',
        medias: [],
        removable: undefined,
        allowChooseMediaFromGallery: undefined,
      });
    });
  });

  const onUndoAllChanges = usePersistFn(async () => {
    await jobManager.resetAndClearCache();
  });

  const onCancelConstructionJob = usePersistFn(async () => {
    if (storeConfiguration.restrictedRolesForCancelConstructionJob?.length) {
      const roleHelper = new OrgUserRoleHelper(profile.user.role);
      if (
        !roleHelper.matchesAny(
          storeConfiguration.restrictedRolesForCancelConstructionJob,
        )
      ) {
        void alert({
          title: '您没有权限取消当前施工项目',
          message: '若需帮助，请联系门店系统管理员！',
        });
        return;
      }
    }

    const confirmed = await confirm({
      title: '确认',
      message:
        '你即将该施工项目，取消后所有数据将会被删除，且无法恢复，是否继续? ',
      confirmButtonText: '是',
      cancelButtonText: '否',
    });
    if (!confirmed) return;

    const cancel = async (confirmedDeliveryCheckIdToBeDeleted?: number) => {
      try {
        appLoading.show();
        await constructionManager.cancelConstructionJob(jobId, {
          confirmedDeliveryCheckIdToBeDeleted,
        });
        appLoading.hide();
        navigation.goBack();
      } catch (e) {
        appLoading.hide();
        if (
          e instanceof HttpError &&
          e.subCode ===
            'cannot_delete_construction_job_with_existing_delivery_check'
        ) {
          const doubleConfirmed = await confirm({
            title: '确认',
            message:
              '该施工项目已经进行了完工交车检查，取消该施工项目将同时取消对应的交车检查项目，是否确认? ',
            confirmButtonText: '是, 继续',
            cancelButtonText: '否',
          });
          if (doubleConfirmed) {
            await cancel(e.extra?.deliveryCheckId);
          }
        }
      }
    };

    await cancel();
  });

  const onUpdateProductAdoptionConfirmation = usePersistFn(() => {
    navigation.push('_confirmProductAddition', {
      jobId,
      jobName,
      jobManager,
      updateMode: true,
    });
  });

  const { showActions } = useConstructionJobActions({
    isDirty: dirty,
    onAddProcedure,
    onUndoAllChanges,
    onCancelConstructionJob,
  });

  const editable = detail.construction.status !== CommonTaskStatus.Finished;

  const confirmProductAdoptionPresented = useRef(false);

  const presentProductAdoptionConfirmation = usePersistFn(() => {
    if (
      productManifestItems?.some(
        item =>
          item.subject === jobName &&
          !staged.context.productConfirmation?.items.some(x =>
            idEquals(x.productId, item.product.id),
          ),
      )
    ) {
      setTimeout(() => {
        navigation.push('_confirmProductAddition', {
          jobId,
          jobName,
          jobManager,
        });
      }, 500);
    }
  });

  useEffect(() => {
    if (
      confirmProductAdoptionPresented.current ||
      productManifestItems == null
    ) {
      return;
    }

    confirmProductAdoptionPresented.current = true;

    if (
      productManifestItems.some(
        item =>
          item.subject === jobName &&
          !staged.context.productConfirmation?.items.some(x =>
            idEquals(x.productId, item.product.id),
          ),
      )
    ) {
      setTimeout(() => {
        navigation.push('_confirmProductAddition', {
          jobId,
          jobName,
          jobManager,
        });
      }, 1000);
    }
  }, [
    jobId,
    jobName,
    jobManager,
    navigation,
    productManifestItems,
    staged.context.productConfirmation?.items,
  ]);

  return {
    profile,
    jobId,
    detail,
    job,
    jobManager,
    staged,
    dirty,
    editable,
    showActions,
    titleLoading,
    isTechModalVisible,
    setIsTechModalVisible,
    productManifestItems,
    onResolveItemPress,
    onTechnicianSelected,
    onTechnicianPickerPress,
    onTechnicianModalDismiss,
    onTakeMedia,
    onDeleteMedia,
    onEditMedia,
    onRetryMediaUpload,
    onPreviewVideo,
    onRemarkTextChange,
    onProcedureMove,
    onRemoveProcedure,
    onSave,
    onFinish,
    onUpdateProductAdoptionConfirmation,
    presentProductAdoptionConfirmation,
  };
};
